import {
  CARE_SOLUTIONS,
  GENDER,
  HOME_CARE_SOLUTIONS,
  HOME_CARE_SPECIALIZATIONS,
  HOSPITAL_SOLUTIONS,
  HOSPITAL_SPECIALIZATIONS_DE,
  MEDICAL_SUPPLIES_SOLUTIONS,
  MOBILE_SOLUTIONS,
  OVERHEIGHT,
  OVERWEIGHT_CARE,
  OVERWEIGHT_HOSPITAL,
  OVERWEIGHT_REHAB,
  PSYCH_CARE_REHAB_SOLUTIONS,
  PSYCHIATRIC_CARE_SOLUTIONS,
  REHA_SPECIALIZATIONS,
  REHABILITATION_PAYMENT_OPTION,
  REHABILITATION_SOLUTIONS,
  SEARCH_TYPE_CARE,
  SEARCH_TYPE_HOME_CARE,
  SEARCH_TYPE_HOSPITAL,
  SEARCH_TYPE_MEDICAL_SUPPLIES,
  SEARCH_TYPE_PSYCHIATRIC_CARE,
  SEARCH_TYPE_REHABILITATION,
  SOLUTION_SHORT_TERM_STATIC_CARE,
  SOLUTION_STATIC_CARE,
} from "core/consts";
import { isPsychCareSearch } from "core/model/auctions";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import {
  AgeRange,
  Auction,
  CareLevel,
  CareLevelInfos as CareLevelInfosType,
  Careseeker,
  GetOntologyType,
  Interval as MinMaxInterval,
  Patient,
  SearchType,
  Solutions,
} from "core/types";
import {
  format,
  fromUnixTime,
  isValid,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
} from "date-fns";
// TODO: move RadioGroupProps / FieldsHolderType to core types or inject
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { RadioGroupProps } from "ds/ui";
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { FieldsHolderType } from "dsl/organisms/PatientMenu/transform";
import { Iterable } from "immutable";
import Translations from "translations/types";
import { PROVIDER_SEARCH_OVERWEIGHT } from "../providerSearch";
import { getAgeFromDate } from "../utils/dates";
import { useGetOntologies } from "../utils/ontologies/hooks";
import { getDecryptedValue } from "./encryption";

type RadioValue = {
  label: string;
  value: boolean | number | string | null | undefined;
};

type AgeIntervalType = { max: number | null; min: number | null };

export const PatientTypeSolutionMap: {
  [patientType in SearchType]: Array<number>;
} = {
  [SEARCH_TYPE_CARE]: CARE_SOLUTIONS,
  [SEARCH_TYPE_HOME_CARE]: HOME_CARE_SOLUTIONS,
  [SEARCH_TYPE_HOSPITAL]: HOSPITAL_SOLUTIONS,
  [SEARCH_TYPE_MEDICAL_SUPPLIES]: MEDICAL_SUPPLIES_SOLUTIONS,
  [SEARCH_TYPE_PSYCHIATRIC_CARE]: PSYCHIATRIC_CARE_SOLUTIONS,
  [SEARCH_TYPE_REHABILITATION]: REHABILITATION_SOLUTIONS,
};

export function showMultipleSpecializations({
  searchType,
}: { searchType?: SearchType } = {}) {
  if (!searchType) return false;
  const show = (
    [SEARCH_TYPE_HOSPITAL, SEARCH_TYPE_REHABILITATION] as SearchType[]
  ).includes(searchType);

  return show;
}

export const getSpecializations = ({
  admin,
  careseeker,
  searchType,
}: {
  admin?: boolean;
  careseeker?: Careseeker;
  searchType: SearchType;
}) => {
  switch (searchType) {
    case SEARCH_TYPE_HOSPITAL:
      if (admin) return HOSPITAL_SPECIALIZATIONS_DE;
      return (
        careseeker?.config?.whitelisted_hospital_specializations ??
        HOSPITAL_SPECIALIZATIONS_DE
      );
    case SEARCH_TYPE_REHABILITATION:
      if (admin) return REHA_SPECIALIZATIONS;
      return (
        careseeker?.config?.whitelisted_reha_specializations ??
        REHA_SPECIALIZATIONS
      );
    case SEARCH_TYPE_HOME_CARE:
      if (admin) return HOME_CARE_SPECIALIZATIONS;
      return (
        careseeker?.config?.whitelisted_home_care_specializations ??
        HOME_CARE_SPECIALIZATIONS
      );
    default:
      return [];
  }
};

export const getSolutionsForPatientType = (searchType: SearchType) =>
  PatientTypeSolutionMap[searchType] || [];

export const AgesCare: { [key: string]: AgeRange } = {
  "12-17": { max: 17, min: 12, name: "12-17" },
  "18-29": { max: 29, min: 18, name: "18-29" },
  "2": { max: 2, min: null, name: "<2" },
  "2-6": { max: 6, min: 2, name: "2-6" },
  "30-39": { max: 39, min: 30, name: "30-39" },
  "40-49": { max: 49, min: 40, name: "40-49" },
  "50-54": { max: 54, min: 50, name: "50-54" },
  "55-59": { max: 59, min: 55, name: "55-59" },
  "60-64": { max: 64, min: 60, name: "60-64" },
  "65-69": { max: 69, min: 65, name: "65-69" },
  "7-11": { max: 11, min: 7, name: "7-11" },
  "70-79": { max: 79, min: 70, name: "70-79" },
  "80-89": { max: 89, min: 80, name: "80-89" },
  "90": { max: null, min: 90, name: ">90" },
};

export const AgesHospital: { [key: string]: AgeRange } = {
  "18": { max: 18, min: null, name: "<18" },
  "18-59": { max: 59, min: 18, name: "18-59" },
  "60-64": { max: 64, min: 60, name: "60-64" },
  "65-69": { max: 69, min: 65, name: "65-69" },
  "70-74": { max: 74, min: 70, name: "70-74" },
  "75-79": { max: 79, min: 75, name: "75-79" },
  "80-89": { max: 89, min: 80, name: "80-89" },
  "90": { max: null, min: 90, name: ">90" },
};

export const AgesProviderSearch: { [key: string]: AgeRange } = AgesCare;

/**
 * Leftover from the protected patient data feature
 * @param patient
 * @returns the patient data hidden/shown depending on the SW's protected patient role
 */
export function useGetPatientData({ patient }: { patient: Patient }): {
  age: number | string | null;
  patientIdentifier: string | null | undefined;
} {
  const patientName = getPatientName(patient);
  const patientIdentifier = patientName || patient.external_id;

  const ageInterval = patient?.profile?.age_interval ?? null;
  const age = patient?.profile?.birth_date?.decrypted
    ? getAgeFromDate(patient.profile.birth_date.decrypted)
    : getAge(ageInterval);

  return { age, patientIdentifier };
}

export function getCareLevelOptions(
  translations: Translations,
  excludedOptions?: Array<CareLevel>,
): Array<RadioValue> {
  const carelevels = [
    { id: "carelevel-one", label: "1", value: 1 },
    { id: "carelevel-two", label: "2", value: 2 },
    { id: "carelevel-three", label: "3", value: 3 },
    { id: "carelevel-four", label: "4", value: 4 },
    { id: "carelevel-five", label: "5", value: 5 },
    { id: "carelevel-zero", label: translations.general.noneR, value: 0 },
    { id: "carelevel-unknown", label: translations.general.unknown, value: -1 },
  ];

  if (excludedOptions?.length) {
    const excludedCarelevels = carelevels.filter(
      (carelevel) => !excludedOptions.includes(carelevel.value as CareLevel),
    );
    return excludedCarelevels;
  }

  return carelevels;
}

export const getSpecializationForPatientType = (
  searchTypes: Array<SearchType>,
): Array<number> => {
  if (!searchTypes?.length) return [];

  const specializations = searchTypes.flatMap(
    (searchType) => getSpecializations({ admin: true, searchType }) || [],
  );

  return [...new Set(specializations)];
};

export function getCareLevelValue(
  careLevel: CareLevelInfosType | null | undefined,
  translations: Translations,
): RadioValue {
  const diagnosisCareLevel = careLevel?.level;
  if (typeof diagnosisCareLevel === "number") {
    const careLevelValue = getCareLevelOptions(translations).find(
      (level) => level.value === diagnosisCareLevel,
    );
    if (careLevelValue !== undefined) return careLevelValue;
    return { label: translations.general.unknown, value: null };
  }
  return { label: translations.general.unknown, value: null };
}

export const getAges = (
  patientType?: SearchType | null | undefined,
): Array<AgeRange> => {
  let ageRanges: {
    [key: string]: AgeRange;
  } = {};

  if (patientType === SEARCH_TYPE_HOSPITAL) {
    {
      ageRanges = AgesHospital;
    }
  } else {
    ageRanges = AgesCare;
  }

  return Object.keys(ageRanges)
    .map((key: any) => {
      const range = ageRanges[key];
      return {
        max: range.max,
        min: range.min,
        name: range.name,
      };
    })
    .sort((a: any, b: any) => a.min - b.min);
};

export const getAge = (
  ageInterval: AgeIntervalType | null | undefined,
  patientType?: SearchType | null | undefined,
): string | null => {
  if (ageInterval) {
    const ageRanges = getAges(patientType);
    const range = ageRanges.find(
      (queriedRange) => queriedRange.min === ageInterval.min,
    );
    if (range) {
      return range.name;
    }
    return ageRanges[ageRanges.length - 1].name;
  }
  return null;
};

export const getAgeIntervalFromRangeStr = (
  rangeStr: string | null,
  patientType?: SearchType | null,
): AgeIntervalType | null => {
  if (!rangeStr) return null;
  const ageRange = getAges(patientType).find((a) => a.name === rangeStr) || {
    max: null,
    min: null,
  };
  const { max, min } = ageRange;
  return {
    max: !min ? max : (max && max + 1) || null,
    min,
  };
};

export const findAgeConfig = ({
  range,
  value,
}: {
  range: { [key: string]: AgeRange };
  value: MinMaxInterval;
}): [string, AgeRange | null] => {
  if (!value) return ["", null];
  const config = Object.entries(range).find(([_, { max, min }]) => {
    if (value?.max == max && value?.min == min) {
      return true;
    }
    return false;
  });
  return config ?? ["", null];
};

export const isCareSolution = (solution: number | null) =>
  CARE_SOLUTIONS.some((val) => val === solution);

export const isHospitalSolution = (solution: number | null | undefined) =>
  HOSPITAL_SOLUTIONS.some((val) => val === solution);

export const isRehabSolution = (solution: number | null) =>
  REHABILITATION_SOLUTIONS.some((val) => val === solution);

export const hasPsychCareRehabSolution = (
  solutions: number[] | null | undefined,
): boolean => {
  if (!solutions) return false;
  return solutions.some((solution) =>
    PSYCH_CARE_REHAB_SOLUTIONS.includes(solution),
  );
};

export const isStaticCare = (solution: number | null) =>
  solution === SOLUTION_STATIC_CARE ||
  solution === SOLUTION_SHORT_TERM_STATIC_CARE;

export const hasStaticCare = (solutions: number[] | null | undefined) =>
  solutions?.some(isStaticCare);

export function getPatientName(patient: Patient | null | undefined) {
  const firstName =
    patient?.profile?.first_name &&
    getDecryptedValue(patient.profile.first_name);
  const lastName =
    patient?.profile?.last_name && getDecryptedValue(patient.profile.last_name);

  return [firstName, lastName].join(" ").trim();
}

export const getGenderIconProps = (
  gender: number | undefined,
  getOntology: GetOntologyType,
): { alt: string; path: string } => {
  switch (gender) {
    case GENDER.MALE:
      return {
        alt: getOntology({ key: GENDER.MALE, type: ONTOLOGIES.GENDER }),
        path: "gender_male.svg",
      };
    case GENDER.FEMALE:
      return {
        alt: getOntology({ key: GENDER.FEMALE, type: ONTOLOGIES.GENDER }),
        path: "gender_female.svg",
      };
    case GENDER.DIVERSE:
      return {
        alt: getOntology({ key: GENDER.DIVERSE, type: ONTOLOGIES.GENDER }),
        path: "gender_non_binary.svg",
      };
    default:
      return { alt: "", path: "" };
  }
};

export const getCostPayerName = ({
  auction,
  fields,
  translations,
}: {
  auction?: Auction | null | undefined;
  fields?: FieldsHolderType | null | undefined;
  translations: Translations;
}) => {
  const payers = auction?.payers;
  const paymentMethod =
    payers?.selected_payment_method || fields?.selected_payment_method;
  const insuranceName = payers?.insurance?.name || fields?.insurance?.name;
  const publicPensionName =
    payers?.public_pension?.name || fields?.public_pension;
  switch (paymentMethod) {
    case REHABILITATION_PAYMENT_OPTION.INSURANCE:
      return insuranceName;
    case REHABILITATION_PAYMENT_OPTION.CIVIL_SERVANTS_AID:
      return translations.patient.patientInformation.civilServantsAid;
    case REHABILITATION_PAYMENT_OPTION.PUBLIC_ACCIDENT_INSURANCE:
      return translations.patient.patientInformation.publicAccidentInsurance;
    case REHABILITATION_PAYMENT_OPTION.PUBLIC_PENSION:
      return publicPensionName;
    case REHABILITATION_PAYMENT_OPTION.SOCIAL_SERVICE_DEPARTMENT:
      return translations.patient.patientInformation.socialServiceDepartment;
    case REHABILITATION_PAYMENT_OPTION.SELF_PAYER:
      return translations.patient.patientInformation.selfPayer;
    case REHABILITATION_PAYMENT_OPTION.OTHER:
      return translations.patient.patientInformation.payersOther;
    default:
      return null;
  }
};

export const isOverweight = (
  patientType: SearchType | undefined,
  isProviderSearch: boolean,
): number => {
  if (isProviderSearch) {
    return PROVIDER_SEARCH_OVERWEIGHT;
  }
  switch (patientType) {
    case SEARCH_TYPE_REHABILITATION:
      return OVERWEIGHT_REHAB;
    case SEARCH_TYPE_HOSPITAL:
      return OVERWEIGHT_HOSPITAL;
    default:
      return OVERWEIGHT_CARE;
  }
};

type WeightRange = {
  max: number;
  min: number;
  name: string;
};

type WeightIntervalType = {
  max: number | null;
  min: number | null;
} | null;

export const WEIGHT_RANGES: Array<WeightRange> = [
  { max: 100, min: 0, name: "0 - 100" },
  { max: 110, min: 101, name: "101 - 110" },
  { max: 120, min: 111, name: "111 - 120" },
  { max: 130, min: 121, name: "121 - 130" },
  { max: 140, min: 131, name: "131 - 140" },
  { max: 150, min: 141, name: "141 - 150" },
  { max: 160, min: 151, name: "151 - 160" },
  { max: 170, min: 161, name: "161 - 170" },
  { max: 180, min: 171, name: "171 - 180" },
  { max: 190, min: 181, name: "181 - 190" },
  { max: 200, min: 191, name: "191 - 200" },
  { max: 210, min: 201, name: "201 - 210" },
  { max: 220, min: 211, name: "211 - 220" },
  { max: 230, min: 221, name: "221 - 230" },
  { max: 240, min: 231, name: "231 - 240" },
  { max: 250, min: 241, name: "241 - 250" },
  { max: 260, min: 251, name: "251 - 260" },
  { max: 270, min: 261, name: "261 - 270" },
  { max: 280, min: 271, name: "271 - 280" },
  { max: 290, min: 281, name: "281 - 290" },
  { max: 300, min: 291, name: "291 - 300" },
  { max: 500, min: 300, name: "301 - 500" },
];

export const getWeightIntervalString = (
  weightInterval: WeightIntervalType | null | undefined,
): string | null => {
  if (!weightInterval) return null;
  const range = WEIGHT_RANGES.find(
    (queriedRange) =>
      queriedRange?.min <= Number(weightInterval) &&
      queriedRange?.max >= Number(weightInterval),
  );
  if (range) {
    return range.name;
  }

  return null;
};

export const getWeightIntervalFromRangeStr = (
  rangeStr: string | null,
): WeightIntervalType | null => {
  if (!rangeStr) return null;

  const weightRange = WEIGHT_RANGES.find((a) => a.name === rangeStr) || {
    max: null,
    min: null,
  };

  const { max, min } = weightRange;

  return {
    max,
    min,
  };
};

export type Interval = "height_interval" | "weight_interval";

export const getInterval = (
  formValue: Patient | null | undefined,
  searchType: SearchType | null | undefined,
  isProviderSearch: boolean,
  interval: Interval,
  translations: Translations,
) => {
  const intervalMinValue = formValue?.profile?.[interval]?.min;
  if (!searchType) return null;

  if (interval === "weight_interval") {
    const intervalMaxValue = formValue?.profile?.[interval]?.max;
    if (formValue?.profile?.[interval]) {
      // covering old weight ranges
      if (!intervalMaxValue) {
        const prefix =
          intervalMinValue === 0
            ? translations.general.lessThan
            : translations.general.moreThan;

        return `${prefix} ${isOverweight(searchType, isProviderSearch)}`;
      }

      return `${intervalMinValue} - ${intervalMaxValue}`;
    }
    return null;
  }

  if (interval === "height_interval") {
    const prefix =
      intervalMinValue === 0
        ? translations.general.smallerThan
        : translations.general.tallerThan;
    if (formValue?.profile?.[interval]) {
      return `${prefix} ${OVERHEIGHT}`;
    }
    return null;
  }
};

export const formatPickUpTime = (value?: AnyObject) => {
  if (!value) return null;
  const formValue = Iterable.isIterable(value) ? value.toJS() : value;

  const time =
    value &&
    setHours(
      setMinutes(
        setSeconds(setMilliseconds(new Date(), 0), 0),
        formValue.minute ?? 0,
      ),
      formValue.hour ?? 0,
    );

  const isValidTime = isValid(time);

  if (!isValidTime) {
    console.error(`pickup time ${JSON.stringify(value)} cannot be parsed`);
    return null;
  }
  return time;
};

export function showSpecializations({
  searchType,
  solutions,
}: {
  searchType?: SearchType;
  solutions?: number[] | null;
} = {}): boolean {
  if (!searchType) return false;

  const baseShow = (
    [
      SEARCH_TYPE_REHABILITATION,
      SEARCH_TYPE_HOSPITAL,
      SEARCH_TYPE_HOME_CARE,
    ] as SearchType[]
  ).includes(searchType);

  // Also show for psych care with rehab solution
  const isPsychCareWithRehab = Boolean(
    isPsychCareSearch(searchType) && hasPsychCareRehabSolution(solutions),
  );

  return baseShow || isPsychCareWithRehab;
}

export const BLOCKED_DUPLICATE_SEARCH_TYPES: SearchType[] = [
  SEARCH_TYPE_CARE,
  SEARCH_TYPE_HOSPITAL,
  SEARCH_TYPE_REHABILITATION,
];

export const checkAllowedDuplicateSearchType = ({
  auctions,
  searchType,
}: {
  auctions: Auction[] | undefined;
  searchType: SearchType;
}): boolean => {
  if (auctions === undefined) {
    return true;
  }

  if (!BLOCKED_DUPLICATE_SEARCH_TYPES.includes(searchType)) return true;

  return !auctions.some((auction) => auction.search_type === searchType);
};

export const hasMobileSolution = (solutions: Solutions | undefined) =>
  solutions?.some((s) => MOBILE_SOLUTIONS.includes(s));

export const useGenderOptions = (): RadioGroupProps["options"] => {
  const getOntologies = useGetOntologies();

  return getOntologies({
    sort: true,
    type: ONTOLOGIES.GENDER_EXTENDED,
  })
    .filter(({ value }) => value !== GENDER.UNSPECIFIED)
    .map(({ name, value }) => ({
      label: name,
      value,
    }));
};

export function getAgeInterval(birthDate: number, searchType?: SearchType) {
  const age = getAgeFromDate(format(fromUnixTime(birthDate), "dd.MM.yyyy"));
  const ageRanges = getAges(searchType);
  return ageRanges.find(({ max, min }) => {
    if (min == null) return age <= (max || 9999);
    if (max == null) return age >= (min || 0);
    return age >= min && age <= max;
  });
}
